/**
 * Production verification org / account menu.
 *
 * Extracted from the prototype rail-foot user chip (app.tsx) for WP1a (#46597)
 * and expanded into the HG3 launch-menu slots. WP7c wires notifications, help,
 * and report-a-bug onto retained product surfaces. WP7a wires Chat: open the
 * dock when Copilot is enabled, else route to Settings → Agents → Chat.
 * WP7b wires Terminal: operator/local toggle of an in-shell dock; non-operators see an
 * honest disabled reason. WP9a settings landing is /settings inside this shell.
 *
 * #46742 — the company/workspace at the rail head is the dropdown. Search and
 * add-workflow sit beside it. The bottom-rail user chip is gone; those options
 * live in this menu. Product mode never toasts placeholder copy. Pending items
 * render disabled.
 */

import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import {
  BugReportDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@excellent/ui";
import { apiCall } from "@excellent/ui/api";
import { otherActions } from "@excellent/ui/api/contracts/other-actions";
import { MoreHorizontal } from "@excellent/ui/icons";
import { Icon, type IconName } from "./icons";
import {
  activateChatEntryInWindow,
  chatEntryTitle,
} from "./chat-entry";
import {
  TERMINAL_DISABLED_REASON,
  activateTerminalInWindow,
  terminalEntryTitle,
  type TerminalAccess,
} from "./terminal-stage";
import { href } from "./nav";
import { openVerificationSearch } from "./search-palette";
import {
  DEMO_ORGANIZATION,
} from "./demo-org";
import { useHostSurface } from "../shell/host-surface";
import "./org-chrome.css";

export type AccountUser = {
  name: string;
  roleLabel: string;
  initials: string;
  email?: string;
};

export type AccountMenuItem = {
  id: string;
  label: string;
  icon: IconName;
  /** Live product path. Omit when the item is a WP7/WP9 slot only. */
  href?: string;
  /** In-shell action (report-a-bug dialog or WP7a chat). */
  action?: "bug-report" | "open-chat" | "open-terminal";
  /** Mounted in chrome; action lands in a later work package. */
  pending?: boolean;
};

export type AccountWorkspace = {
  id: number;
  slug: string;
  name: string;
};

/** Public download page for the packaged macOS app. */
export const DESKTOP_DOWNLOAD_URL = "https://excellent.so/download";

/** Verification intake for a new workflow / agent. In-shell; never /agents chrome. */
export const ADD_WORKFLOW_HREF = "/workflows/new";
export const ADD_AGENT_HREF = href("workflows", "new");

/** Company name in the rail org control. No subtitle. */
export const ORG_DISPLAY_NAME = "Excellent";

/** Cookie holding the active tenant workspace slug; read by server/tenant.ts. */
export const WORKSPACE_COOKIE = "excellent.workspace";

/**
 * HG3 launch menu. Favorites stays a tools-menu item (#46667 keeps it inside
 * the verification shell). Not primary rail IA. Team is the company team
 * surface (#46742).
 */
export const ACCOUNT_MENU_ITEMS: AccountMenuItem[] = [
  { id: "settings", label: "Settings", icon: "cpu", href: "/settings" },
  { id: "help", label: "Help", icon: "info", href: "/help" },
  { id: "bug", label: "Report bug", icon: "flag", action: "bug-report" },
];

export function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]![0] ?? ""}${parts[1]![0] ?? ""}`.toUpperCase();
  }
  const compact = (parts[0] ?? name).replace(/[^A-Za-z0-9]/g, "");
  return (compact.slice(0, 2) || "W").toUpperCase();
}

function OrgAvatar({
  initials,
  imageUrl,
}: {
  initials: string;
  imageUrl?: string | null;
}): ReactNode {
  if (imageUrl) {
    return <img className="vp-org-avatar-img" src={imageUrl} alt="" />;
  }
  return (
    <span className="vp-avatar" aria-hidden="true">
      {initials}
    </span>
  );
}

function OrgTools({
  onSearch,
  onCreate,
  menuItems = false,
}: {
  onSearch: () => void;
  onCreate: () => void;
  menuItems?: boolean;
}): ReactNode {
  return (
    <div className="vp-org-tools" role={menuItems ? "none" : undefined}>
      <button
        type="button"
        role={menuItems ? "menuitem" : undefined}
        tabIndex={menuItems ? -1 : undefined}
        className="vp-icon-btn"
        data-action-id="shell.org-search"
        aria-label="Search"
        title="Search (⌘K or Ctrl+K)"
        onClick={(event) => {
          event.stopPropagation();
          onSearch();
        }}
      >
        <Icon name="search" size={15} />
      </button>
      <button
        type="button"
        role={menuItems ? "menuitem" : undefined}
        tabIndex={menuItems ? -1 : undefined}
        className="vp-icon-btn vp-icon-btn-add"
        data-action-id="shell.org-create"
        aria-label="Connect workflow or agent"
        title="Connect workflow or agent"
        onClick={(event) => {
          event.stopPropagation();
          onCreate();
        }}
      >
        <Icon name="plus" size={15} />
      </button>
    </div>
  );
}

export function AccountMenu({
  user,
  authenticated,
  onNavigate,
  items = ACCOUNT_MENU_ITEMS,
  workspaces = [],
  activeWorkspaceId = null,
  onSwitchWorkspace,
  copilotEnabled = false,
  terminalAccess = { allowed: false, reason: TERMINAL_DISABLED_REASON },
  workspaceImage,
  overflowTrigger = false,
  onLogout,
}: {
  user: AccountUser;
  authenticated: boolean;
  onNavigate: (to: string) => void;
  items?: AccountMenuItem[];
  workspaces?: AccountWorkspace[];
  activeWorkspaceId?: number | null;
  onSwitchWorkspace?: (workspace: AccountWorkspace) => void;
  /** WP7a: Copilot master switch. Off by default, matching ShellData. */
  copilotEnabled?: boolean;
  /** WP7b: HG6 local/operator gate. Default denies (Reviewer). */
  terminalAccess?: TerminalAccess;
  workspaceImage?: string | null;
  /** Use the reference shell's overflow affordance for the compact pane head. */
  overflowTrigger?: boolean;
  onLogout: () => void;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const [bugReportOpen, setBugReportOpen] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const [switchingToDemo, setSwitchingToDemo] = useState(false);
  const [demoSwitchError, setDemoSwitchError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const initialMenuFocusRef = useRef<"first" | "last">("first");
  const menuId = useId();
  const desktop = useHostSurface() === "desktop";

  function focusableMenuItems(): HTMLButtonElement[] {
    return Array.from(
      menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? [],
    ).filter((item) => item.getClientRects().length > 0);
  }

  useEffect(() => {
    if (!open) {
      return;
    }
    const focusTimer = window.setTimeout(() => {
      const items = focusableMenuItems();
      const target = initialMenuFocusRef.current === "last" ? items.at(-1) : items[0];
      target?.focus();
      initialMenuFocusRef.current = "first";
    }, 0);
    const onDoc = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("mousedown", onDoc);
    };
  }, [open]);

  function closeMenuAndRestoreFocus() {
    setOpen(false);
    window.requestAnimationFrame(() => triggerRef.current?.focus());
  }

  function openMenu(position: "first" | "last") {
    initialMenuFocusRef.current = position;
    setOpen(true);
  }

  function onTriggerKeyDown(event: ReactKeyboardEvent<HTMLButtonElement>) {
    if (event.key === "Escape" && open) {
      event.preventDefault();
      event.stopPropagation();
      closeMenuAndRestoreFocus();
      return;
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    openMenu(event.key === "ArrowUp" ? "last" : "first");
  }

  function onMenuKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      closeMenuAndRestoreFocus();
      return;
    }
    if (event.key === "Tab") {
      setOpen(false);
      return;
    }
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
    const items = focusableMenuItems();
    if (items.length === 0) return;
    event.preventDefault();
    const currentIndex = items.indexOf(document.activeElement as HTMLButtonElement);
    let nextIndex = 0;
    if (event.key === "End") nextIndex = items.length - 1;
    if (event.key === "ArrowDown") nextIndex = (currentIndex + 1 + items.length) % items.length;
    if (event.key === "ArrowUp") nextIndex = (currentIndex - 1 + items.length) % items.length;
    items[nextIndex]?.focus();
  }

  function itemDisabled(item: AccountMenuItem): boolean {
    if (item.id === "terminal") return !terminalAccess.allowed;
    return Boolean(item.pending) || (!item.href && !item.action);
  }

  function activate(item: AccountMenuItem) {
    if (itemDisabled(item)) return;
    setOpen(false);
    if (item.action === "bug-report") {
      setBugReportOpen(true);
      return;
    }
    if (item.action === "open-chat") {
      activateChatEntryInWindow(copilotEnabled, onNavigate);
      return;
    }
    if (item.action === "open-terminal") {
      activateTerminalInWindow(terminalAccess);
      return;
    }
    if (item.href) onNavigate(item.href);
  }

  function onSearch() {
    setOpen(false);
    openVerificationSearch();
  }

  function onCreate() {
    setOpen(false);
    setConnectOpen(true);
  }

  function chooseConnection(to: string) {
    setConnectOpen(false);
    onNavigate(to);
  }

  const activeWorkspace = workspaces.find((workspace) => workspace.id === activeWorkspaceId);
  const demoActive = activeWorkspace?.slug === DEMO_ORGANIZATION.slug;
  const orgName = demoActive ? DEMO_ORGANIZATION.name : (activeWorkspace?.name ?? ORG_DISPLAY_NAME);
  const switcherWorkspaces = workspaces.filter(
    (workspace) => workspace.slug !== DEMO_ORGANIZATION.slug,
  );

  function switchWorkspace(workspace: AccountWorkspace) {
    setOpen(false);
    const leavingDemo = demoActive;
    if (workspace.id === activeWorkspaceId && !leavingDemo) return;
    if (onSwitchWorkspace) {
      onSwitchWorkspace(workspace);
      return;
    }
    document.cookie = `${WORKSPACE_COOKIE}=${encodeURIComponent(workspace.slug)}; Path=/; SameSite=Lax`;
    window.location.assign("/");
  }

  async function switchToDemo() {
    if (demoActive || switchingToDemo) return;
    setDemoSwitchError(null);
    setSwitchingToDemo(true);
    try {
      const result = await apiCall(otherActions.workspaceOpenDemo, {});
      if (result.ok) window.location.assign("/");
    } catch (error) {
      setDemoSwitchError(error instanceof Error ? error.message : "Demo Organization did not open.");
    } finally {
      setSwitchingToDemo(false);
    }
  }

  const orgInitials = initialsFromName(orgName);

  return (
    <div className="vp-account vp-org" ref={rootRef} data-org-menu="">
      <div className="vp-org-row">
        <button
          ref={triggerRef}
          type="button"
          className="vp-org-trigger"
          data-action-id="shell.account-menu"
          data-workspace-identity=""
          title={orgName}
          aria-label={`${orgName} menu`}
          aria-expanded={open}
          aria-haspopup="menu"
          aria-controls={menuId}
          data-org-active={demoActive ? "demo" : "real"}
          onClick={() => {
            if (open) setOpen(false);
            else openMenu("first");
          }}
          onKeyDown={onTriggerKeyDown}
        >
          {overflowTrigger ? (
            <MoreHorizontal size={16} aria-hidden="true" />
          ) : (
            <>
              <OrgAvatar initials={orgInitials} imageUrl={demoActive ? null : workspaceImage} />
              <span className="vp-org-name">{orgName}</span>
              <span className="vp-org-caret" aria-hidden="true">
                <Icon name="chevron-down" size={13} />
              </span>
            </>
          )}
        </button>
        <OrgTools onSearch={onSearch} onCreate={onCreate} />
      </div>
      {open ? (
        <div
          ref={menuRef}
          id={menuId}
          className="vp-account-menu"
          role="menu"
          aria-label="Organization and account"
          onKeyDown={onMenuKeyDown}
        >
          <div className="vp-account-menu-head">
            <OrgAvatar initials={orgInitials} imageUrl={workspaceImage} />
            <span className="vp-account-menu-head-copy">
              <span className="vp-account-menu-head-name">{orgName}</span>
              <span className="vp-account-menu-head-meta">
                {demoActive ? "Sample tenant · anyone can open" : "Your organization"}
              </span>
            </span>
            <OrgTools onSearch={onSearch} onCreate={onCreate} menuItems />
          </div>
          {switcherWorkspaces.map((workspace) => (
            <button
              key={workspace.id}
              type="button"
              role="menuitem"
              tabIndex={-1}
              className="vp-nav-item"
              data-action-id="shell.workspace-switch"
              data-org-kind="workspace"
              data-account-workspace={workspace.slug}
              data-active-workspace={workspace.id === activeWorkspaceId ? "true" : undefined}
              aria-current={!demoActive && workspace.id === activeWorkspaceId ? "true" : undefined}
              title={workspace.name}
              onClick={() => switchWorkspace(workspace)}
            >
              <span className="vp-nav-icon">
                <Icon name="users" size={15} />
              </span>
              <span className="vp-nav-label">{workspace.name}</span>
            </button>
          ))}
          <button
            type="button"
            role="menuitem"
            tabIndex={-1}
            className="vp-nav-item"
            data-action-id="shell.org-switch-demo"
            data-org-kind="demo"
            data-account-workspace={DEMO_ORGANIZATION.slug}
            data-active-workspace={demoActive ? "true" : undefined}
            aria-current={demoActive ? "true" : undefined}
            aria-disabled={switchingToDemo ? "true" : undefined}
            title="Demo — sample tenant any company can open"
            onClick={() => void switchToDemo()}
          >
            <span className="vp-nav-icon">
              <Icon name="book" size={15} />
            </span>
            <span className="vp-nav-label">
              {switchingToDemo ? "Opening Demo Organization…" : DEMO_ORGANIZATION.name}
            </span>
            <span className="vp-org-demo-badge">Sample</span>
          </button>
          {demoSwitchError ? (
            <p className="vp-account-menu-head-meta" role="alert">
              {demoSwitchError}
            </p>
          ) : null}
          <div className="vp-menu-sep" role="separator" />
          <div className="vp-account-menu-head" data-account-identity={authenticated ? "authenticated" : "local"}>
            <OrgAvatar initials={user.initials} />
            <span className="vp-account-menu-head-copy">
              <span className="vp-account-menu-head-name">{user.name}</span>
              <span className="vp-account-menu-head-meta">{user.roleLabel}</span>
            </span>
          </div>
          <div className="vp-menu-sep" role="separator" />
          {items.map((item) => {
            const disabled = itemDisabled(item);
            const terminalDenied = item.id === "terminal" && !terminalAccess.allowed;
            return (
              <button
                key={item.id}
                type="button"
                role="menuitem"
                tabIndex={-1}
                className="vp-nav-item"
                data-action-id="shell.account-item"
                data-account-item={item.id}
                data-pending={disabled && !terminalDenied ? "true" : undefined}
                data-copilot={item.id === "chat" ? (copilotEnabled ? "on" : "off") : undefined}
                data-terminal-access={
                  item.id === "terminal" ? (terminalAccess.allowed ? "operator" : "denied") : undefined
                }
                aria-disabled={disabled ? "true" : undefined}
                title={
                  item.id === "terminal"
                    ? terminalEntryTitle(terminalAccess)
                    : disabled
                    ? `${item.label} — lands in a later work package`
                    : item.id === "chat"
                      ? chatEntryTitle(copilotEnabled)
                      : item.label
                }
                onClick={() => activate(item)}
              >
                <span className="vp-nav-icon">
                  <Icon name={item.icon} size={15} />
                </span>
                <span className="vp-nav-label">{item.label}</span>
              </button>
            );
          })}
          {desktop ? null : (
            <>
              <div className="vp-menu-sep" role="separator" />
              <button
                type="button"
                role="menuitem"
                tabIndex={-1}
                className="vp-nav-item"
                data-action-id="shell.account-item"
                data-account-item="download"
                title="Download desktop app"
                onClick={() => {
                  setOpen(false);
                  window.open(DESKTOP_DOWNLOAD_URL, "_blank", "noopener,noreferrer");
                }}
              >
                <span className="vp-nav-icon">
                  <Icon name="download" size={15} />
                </span>
                <span className="vp-nav-label">Download desktop app</span>
              </button>
              <div className="vp-menu-sep" role="separator" />
            </>
          )}
          {authenticated ? (
            <button
              type="button"
              role="menuitem"
              tabIndex={-1}
              className="vp-nav-item"
              data-action-id="shell.account-item"
              data-account-item="logout"
              title="Log out"
              onClick={() => {
                setOpen(false);
                onLogout();
              }}
            >
              <span className="vp-nav-icon">
                <Icon name="logout" size={15} />
              </span>
              <span className="vp-nav-label">Logout</span>
            </button>
          ) : (
            <button
              type="button"
              role="menuitem"
              tabIndex={-1}
              className="vp-nav-item"
              data-action-id="shell.account-item"
              data-account-item="login"
              title="Sign in"
              onClick={() => {
                setOpen(false);
                onNavigate("/login");
              }}
            >
              <span className="vp-nav-icon">
                <Icon name="logout" size={15} />
              </span>
              <span className="vp-nav-label">Sign in</span>
            </button>
          )}
        </div>
      ) : null}
      <BugReportDialog open={bugReportOpen} onOpenChange={setBugReportOpen} />
      <ConnectModal
        open={connectOpen}
        onOpenChange={setConnectOpen}
        onChoose={chooseConnection}
      />
    </div>
  );
}

function ConnectModal({
  open,
  onOpenChange,
  onChoose,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChoose: (to: string) => void;
}): ReactNode {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Connect workflow or agent</DialogTitle>
          <DialogDescription>
            Choose what Excellent should verify next.
          </DialogDescription>
        </DialogHeader>
        <div className="vp-connect-options">
          <button
            type="button"
            className="vp-connect-option"
            data-action-id="shell.connect-workflow"
            onClick={() => onChoose(ADD_WORKFLOW_HREF)}
          >
            <span className="vp-connect-option-icon" aria-hidden="true">
              <Icon name="link" size={16} />
            </span>
            <span className="vp-connect-option-copy">
              <span className="vp-connect-option-title">Connect workflow</span>
              <span className="vp-connect-option-description">
                Add a human or system workflow and name the oracle that verifies it.
              </span>
            </span>
          </button>
          <button
            type="button"
            className="vp-connect-option"
            data-action-id="shell.connect-agent"
            onClick={() => onChoose(ADD_AGENT_HREF)}
          >
            <span className="vp-connect-option-icon" aria-hidden="true">
              <Icon name="cpu" size={16} />
            </span>
            <span className="vp-connect-option-copy">
              <span className="vp-connect-option-title">Connect agent</span>
              <span className="vp-connect-option-description">
                Create an agent workflow that can run repeatedly with verification attached.
              </span>
            </span>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
